import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Clickable calendar date picker with year & month dropdown navigation.
 * value → "yyyy-mm-dd" string (or "")
 * onChange(dateStr) → "yyyy-mm-dd" or "" when cleared
 * Year dropdown spans (current year - 60) → (current year + 5).
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = "Pilih tanggal…",
  disabled = false,
  className = "",
  testid,
  fromYear,
  toYear,
}) {
  const parsed = value ? new Date(`${value}T00:00:00`) : undefined;
  const label = parsed
    ? parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : placeholder;

  const nowY = new Date().getFullYear();
  const yFrom = fromYear ?? nowY - 60;
  const yTo = toYear ?? nowY + 5;

  const handleSelect = (d) => {
    if (!d) { onChange(""); return; }
    const pad = (n) => String(n).padStart(2, "0");
    onChange(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={cn(
            "w-full h-9 flex items-center justify-between gap-2 border border-slate-300 bg-white px-3 text-left text-xs tabular-nums transition-colors hover:border-[#0B1D3A] disabled:opacity-50 disabled:cursor-not-allowed",
            !value && "text-slate-400",
            className,
          )}
        >
          <span className="truncate">{label}</span>
          <CalendarDays size={14} className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 rounded-none bg-white">
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={handleSelect}
          defaultMonth={parsed || new Date()}
          captionLayout="dropdown-buttons"
          fromYear={yFrom}
          toYear={yTo}
          initialFocus
        />
        {value && (
          <div className="border-t border-slate-200 p-2 flex justify-end">
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-rose-600 px-2 py-1"
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
